import classNames from 'classnames'
import { IPeriodTotals } from './helpers'

interface ISummaryProps {
    /** «این ماه» */
    title: string
    /** «۱ شهریور تا ۲۶ شهریور ۱۴۰۵» */
    range: string
    totals: IPeriodTotals
    /** more pages are still on their way; the figures are not final yet */
    isCounting: boolean
    /** the rows ran past what is added up here; says so instead of guessing */
    cappedAt: number | null
    /** a page failed on the way; what is added up is short of the period */
    isPartial: boolean
    /** top-ups that were handed straight on to a box and so are not counted */
    passThroughCount: number
}

function Stat({
    label,
    amount,
    className,
}: {
    label: string
    amount: number
    className: string
}) {
    return (
        <div className="min-w-0 px-2 text-center first:pr-0 last:pl-0">
            <p className="mb-0 text-[11px] leading-5 text-text-secondary">
                {label}
            </p>
            <p
                className={classNames(
                    'mb-0 truncate text-[15px] font-bold leading-6',
                    className
                )}
            >
                <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                    {amount.toLocaleString()}
                </span>
            </p>
        </div>
    )
}

/**
 * What a period came to — the question someone filtering by «این ماه» is
 * usually asking. «تومان» is said once, under the three figures, rather than
 * three times in a row a phone can barely fit.
 */
export default function PeriodSummary({
    title,
    range,
    totals,
    isCounting,
    cappedAt,
    isPartial,
    passThroughCount,
}: ISummaryProps) {
    return (
        <section
            aria-label={`خلاصهٔ ${title}`}
            aria-busy={isCounting}
            className="mx-4 mt-3 rounded-card border border-[#E4E7EB] bg-white px-3 pb-2.5 pt-3"
        >
            <div className="flex items-baseline justify-between gap-2 text-xs leading-5">
                <p className="mb-0 shrink-0 font-bold text-black">{title}</p>
                <p className="mb-0 min-w-0 truncate text-text-secondary">
                    {range}
                </p>
            </div>

            <div
                className={classNames(
                    'mt-2.5 grid grid-cols-3 divide-x divide-x-reverse divide-divider transition-opacity',
                    isCounting && 'opacity-60'
                )}
            >
                <Stat
                    label="دریافتی"
                    amount={totals.received}
                    className="text-success-on"
                />
                <Stat
                    label="پرداختی"
                    amount={totals.paid}
                    className="text-danger-on"
                />
                <Stat
                    label="شارژ کیف پول"
                    amount={totals.wallet}
                    className="text-brand-deep"
                />
            </div>

            <div className="mb-0 mt-2 border-t border-divider pt-2 text-[11px] leading-5 text-text-muted">
                <p className="mb-0">
                    {isCounting
                        ? 'در حال جمع زدن تراکنش‌ها…'
                        : isPartial
                        ? `مبالغ به تومان، فقط از ${totals.count.toLocaleString()} تراکنشی که دریافت شد`
                        : cappedAt !== null
                        ? `مبالغ به تومان، بر اساس ${cappedAt.toLocaleString()} تراکنش آخر این بازه`
                        : `مبالغ به تومان، از ${totals.count.toLocaleString()} تراکنش`}
                </p>
                {/* Otherwise the same money is counted twice: once where it
                    was spent, once as a top-up on the way there. */}
                {!isCounting && passThroughCount > 0 && (
                    <p className="mb-0">
                        شارژهایی که همان لحظه خرج شده‌اند، در «شارژ کیف پول»
                        شمرده نشده‌اند؛ مبلغشان در «پرداختی» آمده است.
                    </p>
                )}
            </div>
        </section>
    )
}
